import React, { useEffect, useMemo, useState } from 'react';

import { toast } from 'react-toastify';

import { useRoleSwitcher } from '../../../../hooks/useRoleSwitcher';
import AppModal from '../../../../ui-components/AppModal';
import Dropdown from '../../../../ui-components/Dropdown';
import TextEditor from '../../../../ui-components/TextEditor';
import type { AppModalActionProps } from '../../../../model/appModalActionProps';

import {
  useEditEmailNotificationMutation,
  useLazyGetEmailNotificationDetailQuery,
} from '../emailNotificationsApi';

import type { EmailNotificationDetail } from '../model';

const SUPER_ADMIN_ROLE_CODES = ['HBADMIN', 'SUPER_ADMIN', 'SUPERADMIN'];

interface EmailNotificationEditModalProps {
  isOpen: boolean;
  emailTemplateId?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

const EmailNotificationEditModal: React.FC<EmailNotificationEditModalProps> = ({
  isOpen,
  emailTemplateId,
  onClose,
  onSuccess,
}) => {
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [status, setStatus] = useState('');
  const { activeRoleCode } = useRoleSwitcher();
  const canManageStatus = SUPER_ADMIN_ROLE_CODES.includes(
    (activeRoleCode || '').trim().toUpperCase()
  );

  const [fetchDetail, { data, isFetching, error }] =
    useLazyGetEmailNotificationDetailQuery();
  const [updateEmail, { isLoading }] = useEditEmailNotificationMutation();

  useEffect(() => {
    if (isOpen && emailTemplateId) {
      fetchDetail(emailTemplateId);
    }
  }, [fetchDetail, emailTemplateId, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSubject('');
      setBodyHtml('');
      setStatus('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const detail = data?.data;
    if (detail) {
      setSubject(detail.subject ?? '');
      setBodyHtml(detail.bodyHtml ?? '');
      setStatus(detail.status ?? 'active');
    }
  }, [data, isOpen]);

  const templateInfo = useMemo<EmailNotificationDetail | undefined>(
    () => data?.data,
    [data]
  );

  const handleSave = async () => {
    if (!emailTemplateId) return;
    try {
      await updateEmail({
        id: emailTemplateId,
        subject,
        bodyHtml,
        ...(canManageStatus && status ? { status } : {}),
      }).unwrap();
      onSuccess?.();
      onClose();
    } catch (saveError: any) {
      toast.error(
        saveError?.data?.message ||
          saveError?.message ||
          'Failed to update email notification'
      );
    }
  };

  const actions: AppModalActionProps[] = [
    {
      label: 'Cancel',
      variant: 'secondary',
      onClick: onClose,
      disabled: isLoading,
    },
    {
      label: 'Save changes',
      variant: 'primary',
      onClick: handleSave,
      loading: isLoading,
      disabled: isLoading,
    },
  ];

  return (
    <AppModal
      isOpen={isOpen}
      title="Edit Email Template"
      onClose={onClose}
      actions={actions}
      hideFooter={false}
      variant="form"
      closeOnOverlayClick
      maxWidth="max-w-3xl"
    >
      {isFetching ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-neutral-500">Loading template...</p>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-error-500">
            Unable to load email template. Please try again.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Template Name
            </label>
            <input
              type="text"
              value={templateInfo?.templateName ?? ''}
              readOnly
              className="input mt-1.5 bg-neutral-50 dark:bg-neutral-900 cursor-not-allowed border-neutral-200 dark:border-neutral-800"
            />
            <p className="mt-1.5 text-xs text-neutral-500">
              Template names are predefined and cannot be changed
            </p>
          </div>

          {canManageStatus ? (
            <div>
              <Dropdown
                label="Status"
                value={status}
                setValue={(value) => setStatus(String(value))}
                options={STATUS_OPTIONS}
                labelKey="label"
                valueKey="value"
                isCreatable={false}
                placeholder="Select status"
              />
              <p className="mt-1.5 text-xs text-neutral-500">
                Only SuperAdmin can activate or inactivate this template.
              </p>
            </div>
          ) : null}

          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Email Subject <span className="text-error-500">*</span>
              </label>
            </div>
            <input
              type="text"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              className="input"
              placeholder="Enter email subject"
              maxLength={255}
            />
            <div className="mt-1.5 flex justify-between items-center">
              <p className="text-xs text-neutral-500">
                {subject.length}/255 characters
              </p>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Email Body <span className="text-error-500">*</span>
              </label>
            </div>
            <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden bg-white dark:bg-neutral-900">
              <TextEditor value={bodyHtml} onChange={setBodyHtml} />
            </div>
            <div className="mt-1.5 flex justify-end">
              <p className="text-xs text-neutral-500">
                {bodyHtml.length.toLocaleString()} / 50,000
              </p>
            </div>
          </div>
        </div>
      )}
    </AppModal>
  );
};

export default EmailNotificationEditModal;
